/* AISLPC Download Center V3.7.7 - smooth 0.4s non-linear disclosures */
(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-download-center-v3]';
  const MOBILE_BREAKPOINT = 1024;
  const DISCLOSURE_DURATION = 400;
  const normalize = (value = '') => String(value).toLowerCase().trim().replace(/\s+/g, ' ');

  class DownloadCenterV3 {
    constructor(root) {
      if (!root || root.dataset.dcv3Initialized === 'true') return;

      root._dcv3 = this;
      this.root = root;
      this.toolbar = root.querySelector('[data-toolbar]');
      this.form = root.querySelector('[data-search-form]');
      this.input = root.querySelector('[data-search-input]');
      this.clearButton = root.querySelector('[data-search-clear]');
      this.suggestions = root.querySelector('[data-search-suggestions]');
      this.typeFilter = root.querySelector('[data-type-filter]');
      this.typeFilterWrap = root.querySelector('[data-type-filter-wrap]');
      this.sourceFilter = root.querySelector('[data-source-filter]');
      this.sourceFilterWrap = root.querySelector('[data-source-filter-wrap]');
      this.deviceSelect = root.querySelector('[data-device-select]');
      this.deviceNav = root.querySelector('[data-device-nav]');
      this.mobileDeviceNav = root.querySelector('[data-mobile-device-nav]');
      this.mobileDeviceLinks = [...root.querySelectorAll('[data-mobile-device-link]')];
      this.mobileTypeChips = root.querySelector('[data-mobile-type-chips]');
      this.mobileSourceChips = root.querySelector('[data-mobile-source-chips]');
      this.mobileSystemOpenMode = root.dataset.mobileSystemOpenMode || 'first';
      this.responsiveHeading = root.querySelector('[data-responsive-heading]');
      this.responsiveDescription = root.querySelector('[data-responsive-description]');
      this.mobileDescriptionTemplate = root.querySelector('[data-mobile-description]');
      this.mobileMediaQuery = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
      this.reduceMotionMediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.desktopDescriptionHtml = this.responsiveDescription?.innerHTML || '';
      this.panelsContainer = root.querySelector('[data-device-panels]');
      this.status = root.querySelector('[data-download-status]');
      this.searchResults = root.querySelector('[data-search-results]');
      this.searchResultsList = root.querySelector('[data-search-results-list]');
      this.searchResultsCount = root.querySelector('[data-search-results-count]');
      this.searchEmpty = root.querySelector('[data-search-empty]');

      this.deviceLinks = [...root.querySelectorAll('[data-device-link]')];
      this.panels = [...root.querySelectorAll('[data-device-panel]')];
      this.systemCards = [...root.querySelectorAll('[data-system-card]')];
      this.versionCards = [...root.querySelectorAll('[data-version-card]')];
      this.devices = [];
      this.systems = [];
      this.versions = [];
      this.deviceByHandle = new Map();
      this.systemByKey = new Map();
      this.versionByKey = new Map();
      this.mobileFilterChips = [];
      this.searchResultItems = [];
      this.activeDevice = '';
      this.activeType = 'all';
      this.activeSource = 'all';
      this.currentQuery = '';
      this.isSearchMode = false;
      this.suggestionItems = [];
      this.activeSuggestionIndex = -1;
      this.deviceIndicator = null;
      this.deviceIndicatorReady = false;
      this.deviceIndicatorResizeObserver = null;
      this.deviceIndicatorResizeHandler = null;
      this.deviceNavigationClickHandler = null;

      this.setupResponsiveCopy();
      this.collectData();
      this.sortDevices();
      this.buildFilters();
      this.bindEvents();
      this.restoreFromUrl();
      this.setupDeviceIndicator();

      root.dataset.dcv3Initialized = 'true';
    }

    setupResponsiveCopy() {
      this.applyResponsiveCopy = () => {
        const isMobile = this.mobileMediaQuery.matches;
        if (this.responsiveHeading) {
          const desktop = this.responsiveHeading.dataset.desktopHeading || '';
          const mobile = this.responsiveHeading.dataset.mobileHeading || desktop;
          this.responsiveHeading.textContent = isMobile ? mobile : desktop;
        }
        if (this.responsiveDescription) {
          const mobileHtml = this.mobileDescriptionTemplate?.innerHTML || this.desktopDescriptionHtml;
          this.responsiveDescription.innerHTML = isMobile ? mobileHtml : this.desktopDescriptionHtml;
        }
        if (this.input) {
          const desktopPlaceholder = this.input.dataset.desktopPlaceholder || this.input.getAttribute('placeholder') || '';
          const mobilePlaceholder = this.input.dataset.mobilePlaceholder || desktopPlaceholder;
          this.input.setAttribute('placeholder', isMobile ? mobilePlaceholder : desktopPlaceholder);
        }
      };

      this.applyResponsiveCopy();
      if (this.mobileMediaQuery.addEventListener) {
        this.mobileMediaQuery.addEventListener('change', this.applyResponsiveCopy);
      } else {
        this.mobileMediaQuery.addListener?.(this.applyResponsiveCopy);
      }
    }

    collectData() {
      const deviceLinksByHandle = new Map(
        this.deviceLinks.map((link) => [link.dataset.deviceHandle || '', link])
      );
      const mobileLinksByHandle = new Map(
        this.mobileDeviceLinks.map((link) => [link.dataset.deviceHandle || '', link])
      );
      const deviceOptionsByHandle = new Map(
        [...(this.deviceSelect?.options || [])].map((option) => [option.value || '', option])
      );

      this.devices = this.panels.map((panel) => {
        const handle = panel.dataset.deviceHandle || '';
        const link = deviceLinksByHandle.get(handle) || null;
        const device = {
          handle,
          title: panel.dataset.deviceTitle || '',
          search: normalize(panel.dataset.searchText),
          sortOrder: Number(panel.dataset.sortOrder || 9999),
          panel,
          link,
          mobileLink: mobileLinksByHandle.get(handle) || null,
          option: deviceOptionsByHandle.get(handle) || null,
          countBadge: link?.querySelector('.dcv3-device-link__count') || null,
          systems: [],
          filterEmpty: panel.querySelector('[data-filter-empty]'),
          enterFrame: null,
          enterTimer: null,
        };
        this.deviceByHandle.set(handle, device);
        return device;
      });

      this.systems = this.systemCards.map((card) => {
        const system = {
          handle: card.dataset.systemHandle || '',
          title: card.dataset.systemTitle || '',
          type: card.dataset.systemType || '',
          typeLabel: card.dataset.systemTypeLabel || '',
          source: card.dataset.systemSource || '',
          sourceLabel: card.dataset.systemSourceLabel || '',
          search: normalize(card.dataset.searchText),
          deviceHandle: card.dataset.deviceHandle || '',
          card,
          historyBody: card.querySelector('[data-history-body]'),
          historyToggle: card.querySelector('[data-history-toggle]'),
          historyLabel: card.querySelector('[data-history-toggle-label]'),
          mobileToggle: card.querySelector('[data-mobile-system-toggle]'),
        };
        this.deviceByHandle.get(system.deviceHandle)?.systems.push(system);
        this.systemByKey.set(this.getSystemKey(system.deviceHandle, system.handle), system);
        return system;
      });

      this.versions = this.versionCards.map((card) => {
        const version = {
          handle: card.dataset.versionHandle || '',
          title: card.dataset.versionTitle || '',
          role: card.dataset.versionRole || 'single',
          systemHandle: card.dataset.systemHandle || '',
          systemTitle: card.dataset.systemTitle || '',
          deviceHandle: card.dataset.deviceHandle || '',
          deviceTitle: card.dataset.deviceTitle || '',
          type: card.dataset.systemType || '',
          typeLabel: card.dataset.systemTypeLabel || '',
          source: card.dataset.systemSource || '',
          sourceLabel: card.dataset.systemSourceLabel || '',
          releaseDate: card.dataset.releaseDate || '',
          search: normalize(card.dataset.searchText),
          card,
        };
        this.versionByKey.set(
          this.getVersionKey(version.deviceHandle, version.systemHandle, version.handle),
          version
        );
        return version;
      });
    }

    getSystemKey(deviceHandle, systemHandle) {
      return `${deviceHandle}::${systemHandle}`;
    }

    getVersionKey(deviceHandle, systemHandle, versionHandle) {
      return `${deviceHandle}::${systemHandle}::${versionHandle}`;
    }

    sortDevices() {
      // Array sorting is stable in supported browsers. Keeping equal sort orders
      // in Liquid's source order makes the server and client default device agree.
      this.devices.sort((a, b) => a.sortOrder - b.sortOrder);

      const desktopLinks = document.createDocumentFragment();
      const mobileLinks = document.createDocumentFragment();
      const panels = document.createDocumentFragment();
      const options = document.createDocumentFragment();

      this.devices.forEach((device) => {
        if (device.link && this.deviceNav) desktopLinks.appendChild(device.link);
        if (device.mobileLink && this.mobileDeviceNav) mobileLinks.appendChild(device.mobileLink);
        if (this.panelsContainer) panels.appendChild(device.panel);
        if (device.option && this.deviceSelect) options.appendChild(device.option);
      });

      this.deviceNav?.appendChild(desktopLinks);
      this.mobileDeviceNav?.appendChild(mobileLinks);
      this.panelsContainer?.appendChild(panels);
      this.deviceSelect?.appendChild(options);

      this.deviceLinks = this.devices.map((device) => device.link).filter(Boolean);
      this.mobileDeviceLinks = this.devices.map((device) => device.mobileLink).filter(Boolean);
      this.panels = this.devices.map((device) => device.panel);
    }

    setupDeviceIndicator() {
      if (!this.deviceNav || !this.deviceLinks.length) return;

      const indicator = document.createElement('span');
      indicator.className = 'dcv3-device-nav__indicator';
      indicator.setAttribute('aria-hidden', 'true');
      this.deviceNav.prepend(indicator);
      this.deviceNav.classList.add('has-sliding-indicator');
      this.deviceIndicator = indicator;

      const reposition = () => {
        const current = this.deviceLinks.find((link) => link.getAttribute('aria-current') === 'true')
          || this.deviceLinks[0];
        this.moveDeviceIndicator(current, false);
      };

      window.requestAnimationFrame(() => {
        reposition();
        window.requestAnimationFrame(() => {
          this.deviceNav.classList.add('is-indicator-ready');
          this.deviceIndicatorReady = true;
        });
      });

      if ('ResizeObserver' in window) {
        this.deviceIndicatorResizeObserver = new ResizeObserver(reposition);
        this.deviceIndicatorResizeObserver.observe(this.deviceNav);
      } else {
        this.deviceIndicatorResizeHandler = reposition;
        window.addEventListener('resize', reposition, { passive: true });
      }

      document.fonts?.ready?.then(reposition);
    }

    moveDeviceIndicator(link, animate = true) {
      if (!this.deviceIndicator || !this.deviceNav || !link) return;
      if (link.offsetWidth === 0 || link.offsetHeight === 0) return;

      const indicator = this.deviceIndicator;
      const shouldAnimate = animate && this.deviceIndicatorReady && !this.reduceMotionMediaQuery.matches;

      indicator.classList.toggle('is-jumping', !shouldAnimate);
      indicator.style.width = `${link.offsetWidth}px`;
      indicator.style.height = `${link.offsetHeight}px`;
      indicator.style.transform = `translate3d(${link.offsetLeft}px, ${link.offsetTop}px, 0)`;

      if (!shouldAnimate) {
        void indicator.offsetWidth;
        window.requestAnimationFrame(() => indicator.classList.remove('is-jumping'));
      }
    }

    populateFilter(select, values) {
      if (!select) return;
      const fragment = document.createDocumentFragment();
      [...values.entries()]
        .sort((a, b) => a[1].localeCompare(b[1]))
        .forEach(([value, label]) => {
          const option = document.createElement('option');
          option.value = value;
          option.textContent = label;
          fragment.appendChild(option);
        });
      select.appendChild(fragment);
    }

    buildMobileFilterChips(container, select, kind) {
      if (!container || !select) return;
      const fragment = document.createDocumentFragment();

      [...select.options].forEach((option) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'dcv3-mobile-filter-chip';
        button.dataset.mobileFilterChip = '';
        button.dataset.filterKind = kind;
        button.dataset.filterValue = option.value;
        button.setAttribute('aria-pressed', option.value === 'all' ? 'true' : 'false');

        const label = document.createElement('span');
        label.className = 'dcv3-mobile-filter-chip__label';
        label.textContent = option.textContent;

        const count = document.createElement('span');
        count.className = 'dcv3-mobile-filter-chip__count';
        count.dataset.filterCount = '';

        button.append(label, count);
        fragment.appendChild(button);
      });

      container.replaceChildren(fragment);
    }

    syncMobileFilterChips() {
      this.mobileFilterChips.forEach((chip) => {
        const current = chip.dataset.filterKind === 'type' ? this.activeType : this.activeSource;
        const active = chip.dataset.filterValue === current;
        chip.classList.toggle('is-active', active);
        chip.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      this.updateMobileFilterChipCounts();
    }

    updateMobileFilterChipCounts() {
      const activeSystems = this.isSearchMode || !this.activeDevice
        ? this.systems
        : (this.deviceByHandle.get(this.activeDevice)?.systems || []);
      const typeCounts = new Map([['all', 0]]);
      const sourceCounts = new Map([['all', 0]]);

      activeSystems.forEach((system) => {
        if (this.activeSource === 'all' || system.source === this.activeSource) {
          typeCounts.set('all', typeCounts.get('all') + 1);
          typeCounts.set(system.type, (typeCounts.get(system.type) || 0) + 1);
        }
        if (this.activeType === 'all' || system.type === this.activeType) {
          sourceCounts.set('all', sourceCounts.get('all') + 1);
          sourceCounts.set(system.source, (sourceCounts.get(system.source) || 0) + 1);
        }
      });

      this.mobileFilterChips.forEach((chip) => {
        const counts = chip.dataset.filterKind === 'type' ? typeCounts : sourceCounts;
        const count = counts.get(chip.dataset.filterValue || 'all') || 0;
        const countNode = chip.querySelector('[data-filter-count]');
        if (countNode) countNode.textContent = String(count);
        chip.classList.toggle('is-empty', count === 0);
      });
    }

    setMobileSystemOpen(system, open) {
      if (!system) return;
      system.card.classList.toggle('is-mobile-open', open);
      system.mobileToggle?.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    syncMobileSystemAccordion(deviceHandle = this.activeDevice, reset = false) {
      const systems = (this.deviceByHandle.get(deviceHandle)?.systems || [])
        .filter((system) => !system.card.hidden);
      if (!systems.length) return;

      if (this.mobileSystemOpenMode === 'all') {
        systems.forEach((system) => this.setMobileSystemOpen(system, true));
        return;
      }

      if (this.mobileSystemOpenMode === 'none') {
        if (reset) systems.forEach((system) => this.setMobileSystemOpen(system, false));
        return;
      }

      const openSystem = !reset
        ? systems.find((system) => system.card.classList.contains('is-mobile-open'))
        : null;
      const target = openSystem || systems[0];
      systems.forEach((system) => this.setMobileSystemOpen(system, system === target));
    }

    toggleMobileSystem(card) {
      if (!card) return;
      const selected = this.systemByKey.get(
        this.getSystemKey(card.dataset.deviceHandle, card.dataset.systemHandle)
      );
      if (!selected) return;
      const willOpen = !selected.card.classList.contains('is-mobile-open');
      (this.deviceByHandle.get(selected.deviceHandle)?.systems || [])
        .forEach((system) => this.setMobileSystemOpen(system, willOpen && system === selected));
    }

    buildFilters() {
      const types = new Map();
      const sources = new Map();

      this.systems.forEach((system) => {
        if (system.type && system.typeLabel && !types.has(system.type)) {
          types.set(system.type, system.typeLabel);
        }
        if (system.source && system.sourceLabel && !sources.has(system.source)) {
          sources.set(system.source, system.sourceLabel);
        }
      });

      this.populateFilter(this.typeFilter, types);
      this.populateFilter(this.sourceFilter, sources);
      this.buildMobileFilterChips(this.mobileTypeChips, this.typeFilter, 'type');
      this.buildMobileFilterChips(this.mobileSourceChips, this.sourceFilter, 'source');
      this.mobileFilterChips = [...this.root.querySelectorAll('[data-mobile-filter-chip]')];

      const showType = types.size > 1;
      const showSource = sources.size > 1;
      if (this.typeFilterWrap) this.typeFilterWrap.hidden = !showType;
      if (this.sourceFilterWrap) this.sourceFilterWrap.hidden = !showSource;
      if (this.mobileTypeChips) this.mobileTypeChips.hidden = !showType;
      if (this.mobileSourceChips) this.mobileSourceChips.hidden = !showSource;

      const filterCount = Number(showType) + Number(showSource);
      this.toolbar?.classList.remove('dcv3-toolbar--filters-0', 'dcv3-toolbar--filters-1', 'dcv3-toolbar--filters-2');
      this.toolbar?.classList.add(`dcv3-toolbar--filters-${filterCount}`);
    }

    bindEvents() {
      this.deviceSelect?.addEventListener('change', () => {
        if (this.deviceSelect.value) this.activateDevice(this.deviceSelect.value, false, true, true);
      });

      // Device navigation uses the capture phase so another theme component
      // cannot block the Download Center by stopping click bubbling.
      this.deviceNavigationClickHandler = (event) => {
        const target = event.target instanceof Element ? event.target : event.target?.parentElement;
        const deviceLink = target?.closest?.('[data-device-link], [data-mobile-device-link]');
        if (!deviceLink || !this.root.contains(deviceLink)) return;

        const handle = deviceLink.dataset.deviceHandle || '';
        if (!handle || !this.deviceByHandle.has(handle)) return;

        event.preventDefault();
        this.activateDevice(handle, false, true, true);
      };
      this.root.addEventListener('click', this.deviceNavigationClickHandler, true);

      this.rootClickHandler = (event) => {
        const target = event.target instanceof Element ? event.target : event.target?.parentElement;
        if (!target) return;

        const releaseNotesSummary = target.closest('.dcv3-version__details > summary');
        if (releaseNotesSummary && this.root.contains(releaseNotesSummary)) {
          const details = releaseNotesSummary.parentElement;
          if (details?.classList.contains('dcv3-version__details')) {
            event.preventDefault();
            this.toggleReleaseNotes(details);
          }
          return;
        }

        const mobileSystemToggle = target.closest('[data-mobile-system-toggle]');
        if (mobileSystemToggle && this.root.contains(mobileSystemToggle)) {
          this.toggleMobileSystem(mobileSystemToggle.closest('[data-system-card]'));
          return;
        }

        const mobileSystemHeader = target.closest('.dcv3-system__header');
        if (
          this.mobileMediaQuery.matches &&
          mobileSystemHeader &&
          this.root.contains(mobileSystemHeader) &&
          !target.closest('a, button, input, select, summary')
        ) {
          this.toggleMobileSystem(mobileSystemHeader.closest('[data-system-card]'));
          return;
        }

        const mobileFilterChip = target.closest('[data-mobile-filter-chip]');
        if (mobileFilterChip && this.root.contains(mobileFilterChip)) {
          const kind = mobileFilterChip.dataset.filterKind;
          const value = mobileFilterChip.dataset.filterValue || 'all';
          if (kind === 'type') {
            this.activeType = value;
            if (this.typeFilter) this.typeFilter.value = value;
          } else {
            this.activeSource = value;
            if (this.sourceFilter) this.sourceFilter.value = value;
          }
          this.applyFilters(true);
          return;
        }

        const historyToggle = target.closest('[data-history-toggle]');
        if (historyToggle && this.root.contains(historyToggle)) {
          const system = historyToggle.closest('[data-system-card]');
          if (system) this.toggleHistory(system.dataset.deviceHandle, system.dataset.systemHandle);
          return;
        }

        const viewButton = target.closest('[data-view-version]');
        if (viewButton && this.root.contains(viewButton)) {
          this.openVersion(
            viewButton.dataset.deviceHandle,
            viewButton.dataset.systemHandle,
            viewButton.dataset.versionHandle,
            true,
            true
          );
          return;
        }

        const suggestion = target.closest('[data-suggestion]');
        if (suggestion && this.root.contains(suggestion)) this.selectSuggestion(suggestion);
      };
      this.root.addEventListener('click', this.rootClickHandler);

      this.form?.addEventListener('submit', (event) => {
        event.preventDefault();
        this.runSearch(this.input?.value || '', true);
      });

      this.input?.addEventListener('input', () => {
        const hasValue = this.input.value.length > 0;
        if (this.clearButton) this.clearButton.hidden = !hasValue;
        this.renderSuggestions(this.input.value);
      });

      this.input?.addEventListener('keydown', (event) => this.handleSuggestionKeys(event));

      this.clearButton?.addEventListener('click', () => {
        this.input.value = '';
        this.clearButton.hidden = true;
        this.closeSuggestions();
        this.exitSearchMode();
        this.applyFilters(false);
        this.updateUrl({ q: null });
        this.input.focus();
      });

      this.typeFilter?.addEventListener('change', () => {
        this.activeType = this.typeFilter.value || 'all';
        this.applyFilters(true);
      });

      this.sourceFilter?.addEventListener('change', () => {
        this.activeSource = this.sourceFilter.value || 'all';
        this.applyFilters(true);
      });

      this.outsideClickHandler = (event) => {
        if (!this.root.contains(event.target)) this.closeSuggestions();
      };
      document.addEventListener('click', this.outsideClickHandler);

      this.popStateHandler = () => this.restoreFromUrl(false);
      window.addEventListener('popstate', this.popStateHandler);
    }

    restoreFromUrl(updateUrl = false) {
      const params = new URL(window.location.href).searchParams;
      const type = params.get('type');
      const source = params.get('source');
      const query = params.get('q');
      const hashDevice = window.location.hash.startsWith('#dcv3-device-')
        ? window.location.hash.replace(/^#dcv3-device-/, '')
        : '';
      const device = params.get('device') || (this.deviceByHandle.has(hashDevice) ? hashDevice : null);
      const system = params.get('system');
      const version = params.get('version');

      const hasTypeOption = type && [...(this.typeFilter?.options || [])]
        .some((option) => option.value === type);
      const hasSourceOption = source && [...(this.sourceFilter?.options || [])]
        .some((option) => option.value === source);

      if (hasTypeOption) {
        this.activeType = type;
        this.typeFilter.value = type;
      }

      if (hasSourceOption) {
        this.activeSource = source;
        this.sourceFilter.value = source;
      }

      if (query) {
        if (this.input) {
          this.input.value = query;
          if (this.clearButton) this.clearButton.hidden = false;
        }
        this.runSearch(query, updateUrl);
        return;
      }

      if (device && system && version) {
        this.openVersion(device, system, version, false, updateUrl);
        return;
      }

      if (device && system) {
        this.openSystem(device, system, false, updateUrl);
        return;
      }

      const initialDevice = device && this.deviceByHandle.has(device)
        ? device
        : this.devices[0]?.handle;
      if (initialDevice) this.activateDevice(initialDevice, false, updateUrl);
    }

    activateDevice(handle, scroll = false, updateUrl = true, animate = false) {
      const device = this.deviceByHandle.get(handle) || this.devices[0];
      if (!device) return;

      const shouldAnimate = animate && !this.reduceMotionMediaQuery.matches;

      this.activeDevice = device.handle;
      this.exitSearchMode();

      this.devices.forEach((item) => {
        const active = item.handle === device.handle;

        if (item.enterFrame) {
          window.cancelAnimationFrame(item.enterFrame);
          item.enterFrame = null;
        }
        if (item.enterTimer) {
          window.clearTimeout(item.enterTimer);
          item.enterTimer = null;
        }

        item.panel.classList.remove('is-enter-preparing', 'is-entering');

        // Put the incoming panel at the hidden animation start position before revealing it.
        if (active && shouldAnimate) item.panel.classList.add('is-enter-preparing');

        item.panel.hidden = !active;
        item.link?.classList.toggle('is-active', active);
        item.link?.setAttribute('aria-current', active ? 'true' : 'false');
      });

      this.mobileDeviceLinks.forEach((link) => {
        const active = link.dataset.deviceHandle === device.handle;
        link.classList.toggle('is-active', active);
        link.setAttribute('aria-current', active ? 'true' : 'false');
        if (active && this.mobileDeviceNav) {
          const left = link.offsetLeft - (this.mobileDeviceNav.clientWidth - link.offsetWidth) / 2;
          this.mobileDeviceNav.scrollTo({ left: Math.max(0, left), behavior: shouldAnimate ? 'smooth' : 'auto' });
        }
      });

      if (shouldAnimate) {
        // Commit the prepared hidden state before starting the entrance animation.
        void device.panel.offsetWidth;

        device.enterFrame = window.requestAnimationFrame(() => {
          device.panel.classList.remove('is-enter-preparing');
          device.panel.classList.add('is-entering');
          device.enterFrame = null;

          // Remove animation-only styles after the longest stagger has completed.
          device.enterTimer = window.setTimeout(() => {
            device.panel.classList.remove('is-entering');
            device.enterTimer = null;
          }, 1150);
        });
      }

      if (device.link) this.moveDeviceIndicator(device.link, true);
      if (this.deviceSelect) this.deviceSelect.value = device.handle;
      this.applyFilters(false, true);

      if (scroll) device.panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
      if (updateUrl) this.updateUrl({ device: device.handle, system: null, version: null, q: null });
    }

    clearDisclosureTransition(content) {
      if (!content) return;

      if (content._dcv3DisclosureEndHandler) {
        content.removeEventListener('transitionend', content._dcv3DisclosureEndHandler);
        content._dcv3DisclosureEndHandler = null;
      }

      if (content._dcv3DisclosureTimer) {
        window.clearTimeout(content._dcv3DisclosureTimer);
        content._dcv3DisclosureTimer = null;
      }
    }

    animateDisclosure(
      content,
      open,
      {
        onOpenStart,
        onCloseEnd,
        hideOnClose = true,
        forceClosedStart = false,
      } = {}
    ) {
      if (!content) return;

      const nextOpen = Boolean(open);
      const wasHidden = content.hidden;
      const needsCommittedClosedFrame = nextOpen && (wasHidden || forceClosedStart);

      this.clearDisclosureTransition(content);
      content.dataset.dcv3DisclosureTarget = nextOpen ? 'open' : 'closed';

      if (nextOpen) {
        content.hidden = false;
        onOpenStart?.();
      }

      if (this.reduceMotionMediaQuery.matches) {
        content.classList.toggle('is-open', nextOpen);
        if (!nextOpen) {
          if (hideOnClose) content.hidden = true;
          onCloseEnd?.();
        }
        return;
      }

      /*
       * First open needs one committed collapsed frame. This is a single,
       * user-triggered layout read; there is no per-frame height measuring.
       * Afterwards CSS owns the full 400 ms transition in both directions.
       */
      if (needsCommittedClosedFrame) {
        content.classList.remove('is-open');
        void content.offsetHeight;
      }

      content.classList.toggle('is-open', nextOpen);

      let finished = false;
      const finish = () => {
        if (finished) return;
        if (content.dataset.dcv3DisclosureTarget !== (nextOpen ? 'open' : 'closed')) return;
        finished = true;
        this.clearDisclosureTransition(content);

        if (!nextOpen) {
          if (hideOnClose) content.hidden = true;
          onCloseEnd?.();
        }
      };

      const onTransitionEnd = (event) => {
        if (event.target !== content || event.propertyName !== 'grid-template-rows') return;
        finish();
      };

      content._dcv3DisclosureEndHandler = onTransitionEnd;
      content.addEventListener('transitionend', onTransitionEnd);
      content._dcv3DisclosureTimer = window.setTimeout(finish, DISCLOSURE_DURATION + 80);
    }

    setHistoryOpen(system, open) {
      if (!system?.historyBody || !system?.historyToggle) return;

      const nextOpen = Boolean(open);
      system.historyToggle.setAttribute('aria-expanded', nextOpen ? 'true' : 'false');
      const count = system.historyToggle.dataset.historyCount || '0';
      if (system.historyLabel) {
        system.historyLabel.textContent = nextOpen
          ? this.root.dataset.hideHistoryLabel
          : `${this.root.dataset.showHistoryLabel} (${count})`;
      }

      this.animateDisclosure(system.historyBody, nextOpen);
    }

    toggleHistory(deviceHandle, systemHandle) {
      const system = this.systemByKey.get(this.getSystemKey(deviceHandle, systemHandle));
      if (!system?.historyBody || !system?.historyToggle) return;
      const isOpen = system.historyToggle.getAttribute('aria-expanded') === 'true';
      this.setHistoryOpen(system, !isOpen);
    }

    toggleReleaseNotes(details) {
      const content = details?.querySelector('.dcv3-version__details-content');
      if (!details || !content) return;

      const storedState = details.dataset.dcv3DisclosureOpen;
      const isOpen = storedState == null ? details.open : storedState === 'true';
      const nextOpen = !isOpen;
      const needsFirstOpenFrame = nextOpen && !details.open;
      details.dataset.dcv3DisclosureOpen = nextOpen ? 'true' : 'false';

      this.animateDisclosure(content, nextOpen, {
        forceClosedStart: needsFirstOpenFrame,
        hideOnClose: false,
        onOpenStart: () => {
          details.open = true;
        },
        onCloseEnd: () => {
          details.open = false;
        },
      });
    }

    openSystem(deviceHandle, systemHandle, scroll = true, updateUrl = true) {
      const system = this.systemByKey.get(this.getSystemKey(deviceHandle, systemHandle));
      if (!system) {
        this.activateDevice(deviceHandle, false, false);
        return;
      }

      if (this.activeType !== 'all' && this.activeType !== system.type) {
        this.activeType = 'all';
        if (this.typeFilter) this.typeFilter.value = 'all';
      }
      if (this.activeSource !== 'all' && this.activeSource !== system.source) {
        this.activeSource = 'all';
        if (this.sourceFilter) this.sourceFilter.value = 'all';
      }

      this.activateDevice(deviceHandle, false, false);
      this.highlight(system.card);
      if (scroll) system.card.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (updateUrl) this.updateUrl({ device: deviceHandle, system: systemHandle, version: null, q: null });
    }

    openVersion(deviceHandle, systemHandle, versionHandle, scroll = true, updateUrl = true) {
      this.openSystem(deviceHandle, systemHandle, false, false);
      const version = this.versionByKey.get(this.getVersionKey(deviceHandle, systemHandle, versionHandle));
      if (!version) return;

      if (version.role === 'history') {
        const system = this.systemByKey.get(this.getSystemKey(deviceHandle, systemHandle));
        this.setHistoryOpen(system, true);
      }

      this.highlight(version.card);
      if (scroll) version.card.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (updateUrl) this.updateUrl({ device: deviceHandle, system: systemHandle, version: versionHandle, q: null });
    }

    runSearch(rawQuery, updateUrl = true) {
      const query = normalize(rawQuery);
      this.closeSuggestions();

      if (!query) {
        this.exitSearchMode();
        this.applyFilters(false);
        if (updateUrl) this.updateUrl({ q: null });
        return;
      }

      this.currentQuery = rawQuery.trim();
      this.isSearchMode = true;
      this.panels.forEach((panel) => { panel.hidden = true; });
      if (this.searchResults) this.searchResults.hidden = false;

      const matches = this.versions
        .map((version) => ({ version, score: this.scoreVersion(version, query) }))
        .filter((entry) => entry.score >= 0)
        .sort((a, b) => b.score - a.score || b.version.releaseDate.localeCompare(a.version.releaseDate));

      this.renderSearchResults(matches.map((entry) => entry.version));
      this.applyFilters(false);
      if (updateUrl) this.updateUrl({ q: this.currentQuery, device: null, system: null, version: null });
    }

    scoreVersion(version, query) {
      const versionTitle = normalize(version.title);
      const systemTitle = normalize(version.systemTitle);
      const deviceTitle = normalize(version.deviceTitle);

      if (versionTitle === query) return 170;
      if (systemTitle === query) return 160;
      if (deviceTitle === query) return 150;
      if (versionTitle.startsWith(query)) return 135;
      if (systemTitle.startsWith(query)) return 125;
      if (deviceTitle.startsWith(query)) return 115;
      if (versionTitle.includes(query)) return 105;
      if (systemTitle.includes(query)) return 95;
      if (deviceTitle.includes(query)) return 85;
      if (version.search.includes(query)) return 60;
      return -1;
    }

    renderSearchResults(versions) {
      if (!this.searchResultsList) return;
      const fragment = document.createDocumentFragment();
      this.searchResultItems = [];

      versions.forEach((version) => {
        const result = document.createElement('article');
        result.className = 'dcv3-search-result';
        result.dataset.searchResult = '';
        result.dataset.systemType = version.type;
        result.dataset.systemSource = version.source;

        const context = document.createElement('div');
        context.className = 'dcv3-search-result__context';

        const path = document.createElement('span');
        path.className = 'dcv3-search-result__path';
        path.textContent = `${version.deviceTitle} › ${version.systemTitle}`;

        const viewButton = document.createElement('button');
        viewButton.type = 'button';
        viewButton.className = 'dcv3-search-result__view';
        viewButton.dataset.viewVersion = '';
        viewButton.dataset.deviceHandle = version.deviceHandle;
        viewButton.dataset.systemHandle = version.systemHandle;
        viewButton.dataset.versionHandle = version.handle;
        viewButton.textContent = this.root.dataset.viewVersionLabel;

        context.append(path, viewButton);

        const clone = version.card.cloneNode(true);
        clone.classList.remove('is-highlighted');
        clone.hidden = false;
        clone.removeAttribute('hidden');
        clone.removeAttribute('data-version-card');
        clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));

        result.append(context, clone);
        fragment.appendChild(result);
        this.searchResultItems.push(result);
      });

      this.searchResultsList.replaceChildren(fragment);
    }

    matchesFilters(type, source) {
      const typeMatch = this.activeType === 'all' || type === this.activeType;
      const sourceMatch = this.activeSource === 'all' || source === this.activeSource;
      return typeMatch && sourceMatch;
    }

    updateDeviceFilterCounts() {
      const filterActive = this.activeType !== 'all' || this.activeSource !== 'all';

      this.devices.forEach((device) => {
        const count = device.systems.reduce(
          (total, system) => total + Number(this.matchesFilters(system.type, system.source)),
          0
        );

        if (!device.link) return;
        if (!device.countBadge) {
          device.countBadge = document.createElement('span');
          device.countBadge.className = 'dcv3-device-link__count';
          device.link.appendChild(device.countBadge);
        }

        device.countBadge.textContent = String(count);
        device.countBadge.hidden = !filterActive && count === 0;
      });
    }

    applyFilters(updateUrl = true, resetMobileAccordion = false) {
      let visibleCount = 0;

      if (this.isSearchMode) {
        this.searchResultItems.forEach((result) => {
          const matches = this.matchesFilters(result.dataset.systemType || '', result.dataset.systemSource || '');
          result.hidden = !matches;
          if (matches) visibleCount += 1;
        });

        if (this.searchResultsCount) {
          this.searchResultsCount.textContent = `${visibleCount} ${visibleCount === 1 ? this.root.dataset.resultSingular : this.root.dataset.resultPlural}`;
        }
        if (this.searchEmpty) this.searchEmpty.hidden = visibleCount !== 0;
      } else {
        const device = this.deviceByHandle.get(this.activeDevice);
        device?.systems.forEach((system) => {
          const matches = this.matchesFilters(system.type, system.source);
          system.card.hidden = !matches;
          if (matches) visibleCount += 1;
        });
        if (device?.filterEmpty) device.filterEmpty.hidden = visibleCount !== 0;
      }

      this.updateDeviceFilterCounts();
      if (!this.isSearchMode) {
        this.syncMobileSystemAccordion(this.activeDevice, resetMobileAccordion);
      }
      this.syncMobileFilterChips();
      this.updateStatus();
      if (updateUrl) this.updateUrl({ type: this.activeType, source: this.activeSource });
    }

    updateStatus() {
      if (!this.status) return;
      if (this.isSearchMode) {
        this.status.textContent = this.currentQuery ? `${this.root.dataset.searchResultsLabel}: “${this.currentQuery}”` : '';
      } else {
        const device = this.deviceByHandle.get(this.activeDevice);
        this.status.textContent = device?.title || '';
      }
    }

    renderSuggestions(rawQuery) {
      const query = normalize(rawQuery);
      if (!this.suggestions || query.length < 1) {
        this.closeSuggestions();
        return;
      }

      const deviceMatches = this.devices
        .map((device) => ({
          kind: 'device',
          title: device.title,
          meta: device.panel.dataset.contentSummary || '',
          deviceHandle: device.handle,
          score: this.scoreText(device.search, device.title, query),
        }))
        .filter((item) => item.score >= 0);

      const systemMatches = this.systems
        .map((system) => ({
          kind: 'system',
          title: system.title,
          meta: [
            this.deviceByHandle.get(system.deviceHandle)?.title || '',
            system.typeLabel,
            system.sourceLabel,
          ].filter(Boolean).join(' · '),
          deviceHandle: system.deviceHandle,
          systemHandle: system.handle,
          score: this.scoreText(system.search, system.title, query),
        }))
        .filter((item) => item.score >= 0);

      const versionMatches = this.versions
        .map((version) => ({
          kind: 'version',
          title: version.title,
          meta: `${version.deviceTitle} · ${version.systemTitle}`,
          deviceHandle: version.deviceHandle,
          systemHandle: version.systemHandle,
          versionHandle: version.handle,
          score: this.scoreVersion(version, query),
        }))
        .filter((item) => item.score >= 0);

      const results = [...deviceMatches, ...systemMatches, ...versionMatches]
        .sort((a, b) => b.score - a.score)
        .slice(0, 9);

      const fragment = document.createDocumentFragment();
      this.suggestionItems = [];
      results.forEach((result) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'dcv3-suggestion';
        button.dataset.suggestion = '';
        button.dataset.kind = result.kind;
        button.dataset.deviceHandle = result.deviceHandle || '';
        button.dataset.systemHandle = result.systemHandle || '';
        button.dataset.versionHandle = result.versionHandle || '';
        button.setAttribute('role', 'option');

        const text = document.createElement('span');
        const title = document.createElement('span');
        const meta = document.createElement('span');
        const kind = document.createElement('span');

        title.className = 'dcv3-suggestion__title';
        meta.className = 'dcv3-suggestion__meta';
        kind.className = 'dcv3-suggestion__kind';
        title.textContent = result.title;
        meta.textContent = result.meta;
        kind.textContent = this.root.dataset[`${result.kind}Label`];

        text.append(title, meta);
        button.append(text, kind);
        fragment.appendChild(button);
        this.suggestionItems.push(button);
      });

      this.suggestions.replaceChildren(fragment);
      this.activeSuggestionIndex = -1;
      this.suggestions.hidden = results.length === 0;
      this.input?.setAttribute('aria-expanded', results.length ? 'true' : 'false');
    }

    scoreText(searchText, title, query) {
      const normalizedTitle = normalize(title);
      if (normalizedTitle === query) return 150;
      if (normalizedTitle.startsWith(query)) return 120;
      if (normalizedTitle.includes(query)) return 95;
      if (normalize(searchText).includes(query)) return 60;
      return -1;
    }

    handleSuggestionKeys(event) {
      if (!this.suggestionItems.length || this.suggestions?.hidden) {
        if (event.key === 'Escape') this.closeSuggestions();
        return;
      }

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const direction = event.key === 'ArrowDown' ? 1 : -1;
        this.activeSuggestionIndex = (this.activeSuggestionIndex + direction + this.suggestionItems.length) % this.suggestionItems.length;
        this.updateActiveSuggestion();
      } else if (event.key === 'Enter' && this.activeSuggestionIndex >= 0) {
        event.preventDefault();
        this.selectSuggestion(this.suggestionItems[this.activeSuggestionIndex]);
      } else if (event.key === 'Escape') {
        this.closeSuggestions();
      }
    }

    updateActiveSuggestion() {
      this.suggestionItems.forEach((item, index) => {
        const active = index === this.activeSuggestionIndex;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-selected', active ? 'true' : 'false');
        if (active) item.scrollIntoView({ block: 'nearest' });
      });
    }

    selectSuggestion(item) {
      this.closeSuggestions();
      const kind = item.dataset.kind;

      if (kind === 'device') {
        this.activateDevice(item.dataset.deviceHandle, true, true);
      } else if (kind === 'system') {
        this.openSystem(item.dataset.deviceHandle, item.dataset.systemHandle, true, true);
      } else {
        this.openVersion(item.dataset.deviceHandle, item.dataset.systemHandle, item.dataset.versionHandle, true, true);
      }
    }

    exitSearchMode() {
      this.isSearchMode = false;
      this.currentQuery = '';
      if (this.searchResults) this.searchResults.hidden = true;
      if (this.searchResultsList) this.searchResultsList.replaceChildren();
      this.searchResultItems = [];
      const activePanel = this.deviceByHandle.get(this.activeDevice)?.panel;
      if (activePanel) activePanel.hidden = false;
    }

    highlight(element) {
      if (!element) return;
      element.classList.remove('is-highlighted');
      window.requestAnimationFrame(() => element.classList.add('is-highlighted'));
      window.setTimeout(() => element.classList.remove('is-highlighted'), 2200);
    }

    closeSuggestions() {
      if (this.suggestions) {
        this.suggestions.hidden = true;
        this.suggestions.replaceChildren();
      }
      this.suggestionItems = [];
      this.activeSuggestionIndex = -1;
      this.input?.setAttribute('aria-expanded', 'false');
    }

    updateUrl(changes) {
      const url = new URL(window.location.href);
      url.hash = '';

      const entries = {
        q: changes.q,
        device: changes.device,
        system: changes.system,
        version: changes.version,
        type: changes.type ?? this.activeType,
        source: changes.source ?? this.activeSource,
      };

      Object.entries(entries).forEach(([key, value]) => {
        if (value && value !== 'all') url.searchParams.set(key, value);
        else if (value === null || value === '' || value === 'all') url.searchParams.delete(key);
      });

      window.history.replaceState({}, '', url);
    }

    destroy() {
      document.removeEventListener('click', this.outsideClickHandler);
      window.removeEventListener('popstate', this.popStateHandler);

      if (this.deviceNavigationClickHandler) {
        this.root.removeEventListener('click', this.deviceNavigationClickHandler, true);
        this.deviceNavigationClickHandler = null;
      }

      if (this.rootClickHandler) {
        this.root.removeEventListener('click', this.rootClickHandler);
        this.rootClickHandler = null;
      }

      if (this.mobileMediaQuery?.removeEventListener) {
        this.mobileMediaQuery.removeEventListener('change', this.applyResponsiveCopy);
      } else {
        this.mobileMediaQuery?.removeListener?.(this.applyResponsiveCopy);
      }

      this.deviceIndicatorResizeObserver?.disconnect();
      if (this.deviceIndicatorResizeHandler) {
        window.removeEventListener('resize', this.deviceIndicatorResizeHandler);
      }


      this.devices.forEach((device) => {
        if (device.enterFrame) window.cancelAnimationFrame(device.enterFrame);
        if (device.enterTimer) window.clearTimeout(device.enterTimer);
      });

      this.root.querySelectorAll('[data-history-body], .dcv3-version__details-content').forEach((content) => {
        this.clearDisclosureTransition(content);
        delete content.dataset.dcv3DisclosureTarget;
      });

      this.deviceIndicator?.remove();
      this.deviceNav?.classList.remove('has-sliding-indicator', 'is-indicator-ready');
      this.root.dataset.dcv3Initialized = 'false';
      delete this.root._dcv3;
    }
  }

  const init = (scope = document) => {
    scope.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (root.dataset.dcv3Initialized === 'true') return;

      try {
        new DownloadCenterV3(root);
      } catch (error) {
        root.dataset.dcv3Initialized = 'false';
        delete root._dcv3;
        console.error('[AISLPC Download Center] Initialization failed:', error);
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll(ROOT_SELECTOR).forEach((root) => root._dcv3?.destroy?.());
  });
})();
